import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { API } from "@/lib/scroll";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Blog() {
  usePageMeta(
    "Insights — Marketing Thinking That Compounds | Build Right Partners",
    "Field notes on Google Ads, SEO, Yelp, branding, and growth from the partners at Build Right Partners, a California marketing agency."
  );
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API}/posts`)
      .then(({ data }) => setPosts(data.posts))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PageShell testid="blog-page" overline="Insights & Field Notes" title="Thinking that" italic="compounds.">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28">
        {loading && (
          <p className="mt-8 font-mono text-xs uppercase tracking-[0.3em] text-slate-500">Loading…</p>
        )}
        {!loading && posts.length === 0 && (
          <div className="mt-8 border border-amber-500/15 bg-[#0A1128]/60 p-12 text-center">
            <p className="font-serif text-2xl text-amber-100 italic">No posts yet.</p>
            <p className="mt-2 text-sm text-slate-400">New thinking is being drafted — check back soon.</p>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-px bg-amber-500/10 border border-amber-500/10 mt-8">
          {posts.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`blog-post-${i + 1}`}
              onClick={() => navigate(`/blog/${p.id}`)}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="group relative bg-[#0A1128] p-10 lg:p-12 hover:bg-[#0F1A3A] transition-colors duration-500 cursor-pointer"
            >
              <span className="absolute top-0 left-0 h-[2px] w-0 bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] transition-all duration-700 group-hover:w-full" />
              <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.25em]">
                <span className="text-amber-400/90">{p.tag}</span>
                <span className="text-slate-500">
                  {p.created_at ? new Date(p.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : ""}
                </span>
                {p.read_time && <span className="text-slate-600">· {p.read_time}</span>}
              </div>
              <h2 className="mt-6 font-serif text-2xl lg:text-3xl font-semibold text-amber-50 leading-snug flex items-start gap-3">
                {p.title}
                <ArrowUpRight size={20} className="shrink-0 mt-1.5 text-amber-400 opacity-0 -translate-x-2 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0" />
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-300/80 max-w-lg">{p.excerpt}</p>
            </motion.article>
          ))}
        </div>
        <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-slate-600">
          New field notes every month — written by partners, not ghostwriters.
        </p>
      </section>
    </PageShell>
  );
}
